import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import StatCard from './StatCard';
import {
  compileHealthTone,
  type CodeCount,
  type DaySeriesPoint,
  type QueueRow,
  type RangeTotals,
  type DailyStopRow,
  summarizeStops,
} from '@/lib/writer/telemetryTypes';

interface Props {
  series: DaySeriesPoint[];
  totals: RangeTotals;
  failureCodes: CodeCount[];
  queue: QueueRow | null;
  dailyStops: DailyStopRow[];
}

const fmtMs = (ms: number | null) => (ms === null ? '—' : `${(ms / 1000).toFixed(1)}s`);

const ReliabilityPanel: React.FC<Props> = ({ series, totals, failureCodes, queue, dailyStops }) => {
  const stops = summarizeStops(dailyStops);
  const chartData = series.map((p) => ({
    day: p.day.slice(5),
    success: p.compilesSuccess,
    failed: p.compilesFailed,
    pending: p.compilesOther,
    p90: p.jobP90Ms ? Number((p.jobP90Ms / 1000).toFixed(2)) : 0,
  }));

  const p50s = series.map((p) => p.jobP50Ms).filter((x): x is number => x != null);
  const p90s = series.map((p) => p.jobP90Ms).filter((x): x is number => x != null);
  const medP50 = p50s.length ? p50s.reduce((a, b) => a + b, 0) / p50s.length : null;
  const medP90 = p90s.length ? p90s.reduce((a, b) => a + b, 0) / p90s.length : null;

  const stale =
    queue?.oldest_pending_at &&
    Date.now() - new Date(queue.oldest_pending_at).getTime() > 10 * 60 * 1000;

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Reliability</h2>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Compile success rate"
          tone={compileHealthTone(totals.compileSuccessRate)}
          value={
            totals.compileSuccessRate === null
              ? '—'
              : `${(totals.compileSuccessRate * 100).toFixed(0)}%`
          }
          hint={`${totals.compilesFailed} failed`}
        />
        <StatCard label="Time to PDF (median)" value={fmtMs(medP50)} hint={`p90 ${fmtMs(medP90)}`} />
        <StatCard
          label="Queue pending"
          tone={stale ? 'bad' : (queue?.queued ?? 0) > 5 ? 'warn' : 'ok'}
          value={`${(queue?.queued ?? 0) + (queue?.running ?? 0)}`}
          hint={
            queue?.oldest_pending_at
              ? `oldest ${new Date(queue.oldest_pending_at).toLocaleString()}`
              : 'nothing waiting'
          }
        />
        <StatCard
          label="Iteration-cap hits"
          tone={
            stops.capHitRate === null || stops.capHitRate < 0.05
              ? 'ok'
              : stops.capHitRate < 0.15
                ? 'warn'
                : 'bad'
          }
          value={
            stops.capHitRate === null
              ? '—'
              : `${(stops.capHitRate * 100).toFixed(0)}%`
          }
          hint={
            stops.capHits === 0
              ? 'every turn finished on its own'
              : `${stops.capHits} turns · ${stops.rescued} rescued · ${stops.rescueFailed} still empty`
          }
        />
        <StatCard
          label="Queue failures (all time)"
          tone={(queue?.failed ?? 0) > 0 ? 'warn' : 'ok'}
          value={`${queue?.failed ?? 0}`}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Compiles per day</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="l" tick={{ fontSize: 11 }} width={36} />
                <YAxis
                  yAxisId="r"
                  orientation="right"
                  tick={{ fontSize: 11 }}
                  width={44}
                  tickFormatter={(v) => `${v}s`}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="l" dataKey="success" stackId="c" fill="#10b981" name="success" />
                <Bar yAxisId="l" dataKey="failed" stackId="c" fill="#ef4444" name="failed" />
                <Bar yAxisId="l" dataKey="pending" stackId="c" fill="#94a3b8" name="pending" />
                <Line
                  yAxisId="r"
                  type="monotone"
                  dataKey="p90"
                  stroke="#4f46e5"
                  dot={false}
                  name="p90 compile time"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Top compile failure codes</CardTitle>
        </CardHeader>
        <CardContent>
          {failureCodes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No diagnostics recorded for failed compiles in this range.
            </p>
          ) : (
            <ul className="text-sm font-mono">
              {failureCodes.slice(0, 12).map((c) => (
                <li key={c.code} className="flex justify-between border-b border-border/40 py-1.5">
                  <span>{c.code}</span>
                  <span className="text-muted-foreground tabular-nums">{c.count}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default ReliabilityPanel;
